import type { MutableRefObject } from 'react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useError } from '../useError'
import type { ConfirmDialogOptions } from '../useConfirmDialog'
import { useConfirmDialog } from '../useConfirmDialog'

export type InlineEditorOnClick = () => void
export type UseInlineEditorOptions = {
  saveOnEnterKey?: boolean
  confirmBeforeSaveOptions?: ConfirmDialogOptions
}
export type UseInlineEditorUpdate = (value: any, confirmResult?: any) => Promise<void>

export const useInlineEditor = (
  initialValue = '',
  update: UseInlineEditorUpdate,
  inputRef: MutableRefObject<any>,
  { saveOnEnterKey = true, confirmBeforeSaveOptions }: UseInlineEditorOptions = {}
) => {
  const [editable, setEditable] = useState(false)
  const [saving, setSavingState] = useState(false)
  // The flag the re-entrancy guard reads, kept in a ref for the same reason as `draftRef` below:
  // `setSavingState(true)` is invisible to a second `saveNow` in the SAME event turn, and the inline
  // inputs generate exactly that — Enter saves, the input disables, Chrome blurs the disabled input,
  // and `onBlur` → `saveNow` runs before React has re-rendered. A guard reading the render closure
  // would let that second save through. The writer keeps the `setSaving` name so no call site can
  // reach the raw state setter and leave the ref behind.
  const savingRef = useRef(false)
  const setSaving = (value: boolean) => {
    savingRef.current = value
    setSavingState(value)
  }
  const [newValue, setNewValue] = useState(initialValue)
  // The draft the SAVE reads, kept in a ref so it is current the instant it changes rather than at
  // the next render. react-select's creatable layer hands a TAB to `onCreateOption`, which sets the
  // draft and moves focus in the SAME event turn (`libs/webui/input/src/Select/
  // SelectInlineInput.tsx:100-102`), so `onBlur` → `saveNow` runs before React has re-rendered. A
  // save reading the render closure would persist the pre-create draft and the created option would
  // be silently lost. Every writer goes through `setDraft`, so the ref and the state never diverge.
  const draftRef = useRef(initialValue)
  const setDraft = (value: any) => {
    draftRef.current = value
    setNewValue(value)
  }
  const { onError, error, setError } = useError()
  useEffect(() => setDraft(initialValue), [initialValue])

  const { confirm } = useConfirmDialog(confirmBeforeSaveOptions)

  useEffect(() => {
    if (editable) focus()
    // focus intentionally excluded: it is redefined every render, so including it would call
    // inputRef.current.focus()/.select() on every render while editable is true — stealing focus and
    // resetting the text selection out from under the user as they type. Only entering edit mode
    // (editable flipping true) should trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable])

  const focus = () => {
    inputRef?.current?.focus()
    inputRef?.current?.select()
  }
  const onClick: InlineEditorOnClick = () => {
    setEditable(true)
  }

  const _save = async (args?: any) => {
    try {
      setError(null)
      await update(draftRef.current, args)
      setEditable(false)
    } catch (e) {
      onError(e)
      setEditable(true)
    } finally {
      setSaving(false)
    }
  }

  const saveNow = async (args?: any) => {
    // One save at a time. The inline inputs disable while `submitting`, and Chrome blurs a
    // focused input once it is disabled, so Enter is followed by onBlur calling saveNow again
    // (a click away mid-save does the same). A repeated child-array write fails the parent's
    // version check. Set before the confirm dialog: the dialog takes focus when it opens, and
    // that blur calls saveNow again, which must not open a second dialog. _save's finally and
    // cancelNow clear it.
    if (savingRef.current) return
    setSaving(true)

    //if there is a value for confirmBeforeSaveOptions, it means we want the confirmation dialog to appear first

    if (confirmBeforeSaveOptions) {
      // Forward the dialog's resolved value (e.g. NO=false / YES=true) into _save so it
      // reaches updateParams. An explicit `args` still wins; no caller passes one today.
      return confirm((confirmResult) => _save(args ?? confirmResult), cancelNow, confirmBeforeSaveOptions)
    } else {
      return _save(args)
    }
  }
  const cancelNow = async () => {
    setError(null)
    setDraft(initialValue)
    setEditable(false)
    setSaving(false)
  }
  const onBlur = async () => {
    return saveNow()
  }
  const onKeyDown = async (e: React.KeyboardEvent<any>) => {
    if (e.key === 'Escape') {
      // react-select runs this handler first and only skips its own Escape branch when the
      // event's default was prevented. Left unprevented, that branch clears the value on a
      // closed menu (isClearable + escapeClearsValue), firing onChange one line AFTER the
      // revert below — so the empty value would win and outlive the editor closing.
      e.preventDefault()
      cancelNow()
    } else if (e.key === 'Enter' && saveOnEnterKey) {
      //if there is shift key, it means the user is trying to create a new line, so just skip it
      if (e.shiftKey) {
        return
      }

      //the default enter behavior does not work well with "ConfirmDialog" so we handle it when confirm is available
      if (confirmBeforeSaveOptions) {
        e.preventDefault()
      }
      return saveNow()
    }
  }

  return {
    editable,
    setEditable,
    saving,
    error,
    newValue,
    // `setDraft`, not the raw state setter — a consumer's write must reach the ref the save reads.
    // Signature is unchanged for every call site: all of them pass a plain value, never an updater.
    setNewValue: setDraft,
    onClick,
    onBlur,
    onKeyDown,
    focus,
    inputRef,
    saveNow,
    cancelNow,
  }
}
